import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Edit2, Trash2, Check, X } from "lucide-react";
import { useReservationCategories } from "@/hooks/reservations/useReservationCategories";
import { useCreateReservationCategory } from "@/hooks/reservations/useCreateReservationCategory";
import { useUpdateReservationCategory } from "@/hooks/reservations/useUpdateReservationCategory";
import { useDeleteReservationCategory } from "@/hooks/reservations/useDeleteReservationCategory";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface CategoryManagerProps {
  gymId: string;
}

export const CategoryManager = ({ gymId }: CategoryManagerProps) => {
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newCategory, setNewCategory] = useState({
    name: "",
    description: "",
  });
  const [editCategory, setEditCategory] = useState({
    name: "",
    description: "",
  });

  // Debug logging (temporary)
  console.log('[CategoryManager] Received gymId:', gymId);

  const { data: categories, isLoading } = useReservationCategories(gymId);
  const { mutate: createCategory, isPending } = useCreateReservationCategory();
  const { mutate: updateCategory, isPending: isUpdating } = useUpdateReservationCategory();
  const { mutate: deleteCategory, isPending: isDeleting } = useDeleteReservationCategory();

  // Debug logging for categories
  console.log('[CategoryManager] Categories state:', {
    categoriesCount: categories?.length || 0,
    isLoading,
    gymId
  });

  const handleCreate = () => {
    if (!newCategory.name.trim()) return;

    createCategory({
      name: newCategory.name.trim(),
      description: newCategory.description.trim() || undefined,
      gym_id: gymId,
    }, {
      onSuccess: () => {
        setNewCategory({ name: "", description: "" });
        setIsCreating(false);
      },
    });
  };

  const handleCancel = () => {
    setNewCategory({ name: "", description: "" });
    setIsCreating(false);
  };

  const handleEdit = (category: any) => {
    setEditingId(category.id);
    setEditCategory({
      name: category.name,
      description: category.description || "",
    });
  };

  const handleUpdateCategory = () => {
    if (!editingId || !editCategory.name.trim()) return;

    updateCategory({
      id: editingId,
      name: editCategory.name.trim(),
      description: editCategory.description.trim() || undefined,
    }, {
      onSuccess: () => {
        setEditingId(null);
        setEditCategory({ name: "", description: "" });
      },
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditCategory({ name: "", description: "" });
  };

  const handleDelete = (categoryId: string) => {
    deleteCategory(categoryId);
  };

  if (isLoading) {
    return <div>Cargando categorías...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-md font-medium">Categorías de Reserva</h4>
        <Button
          onClick={() => setIsCreating(true)}
          size="sm"
          disabled={isCreating}
        >
          <Plus className="h-4 w-4 mr-2" />
          Nueva Categoría
        </Button>
      </div>

      {isCreating && (
        <Card>
          <CardContent className="p-4 space-y-4">
            <Input
              placeholder="Nombre de la categoría (ej: Canchas de Fútbol)"
              value={newCategory.name}
              onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
            />
            <Textarea
              placeholder="Descripción (opcional)"
              value={newCategory.description}
              onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
            />
            <div className="flex gap-2">
              <Button
                onClick={handleCreate}
                disabled={!newCategory.name.trim() || isPending}
                size="sm"
              >
                {isPending ? "Creando..." : "Crear"}
              </Button>
              <Button
                variant="outline"
                onClick={handleCancel}
                size="sm"
              >
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {categories?.map((category) => (
          <Card key={category.id}>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  {editingId === category.id ? (
                    <div className="space-y-3">
                      <Input
                        placeholder="Nombre de la categoría"
                        value={editCategory.name}
                        onChange={(e) => setEditCategory(prev => ({ ...prev, name: e.target.value }))}
                      />
                      <Textarea
                        placeholder="Descripción (opcional)"
                        value={editCategory.description}
                        onChange={(e) => setEditCategory(prev => ({ ...prev, description: e.target.value }))}
                      />
                      <div className="flex gap-2">
                        <Button
                          onClick={handleUpdateCategory}
                          disabled={!editCategory.name.trim() || isUpdating}
                          size="sm"
                        >
                          <Check className="h-4 w-4 mr-2" />
                          {isUpdating ? "Guardando..." : "Guardar"}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={handleCancelEdit}
                          size="sm"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <CardTitle className="text-lg">{category.name}</CardTitle>
                      {category.description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {category.description}
                        </p>
                      )}
                    </>
                  )}
                </div>
                {editingId !== category.id && (
                  <div className="flex gap-2">
                    <Badge variant="secondary">
                      {category.is_active ? "Activa" : "Inactiva"}
                    </Badge>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleEdit(category)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar categoría?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta acción eliminará la categoría "{category.name}" y todos sus elementos asociados. 
                            Esta acción no se puede deshacer.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(category.id)}
                            disabled={isDeleting}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {isDeleting ? "Eliminando..." : "Eliminar"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>
            </CardHeader>
          </Card>
        ))}

        {categories?.length === 0 && !isCreating && (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-muted-foreground">
                No hay categorías creadas. Crea tu primera categoría para comenzar.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};